import {inlineError} from '@s-ui/decorators'

import {GetIsMakeHiredValueObject} from '../ValueObjects/GetIsMakeHiredValueObject.js'
import DealerRepository from './DealerRepository.js'

/**
 * @class
 * @implements DealerRepository
 */
export class HTTPDealerRepository extends DealerRepository {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Config} deps.config
   * @param {FromDealerRawDataToDealerEntityMapper} deps.fromDealerRawDataToDealerEntityMapper
   * @param {Function} deps.contactDealerErrorFactory
   * @param {Function} deps.resultValueObjectFactory
   * @param {Function} deps.trackingPhoneDealerValueObjectFactory
   * @param {Function} deps.trackingPhoneErrorFactory
   * @param {Function} deps.getDealerErrorFactory
   * @param {Function} deps.searchResultsDealerErrorFactory
   * @param {Function} deps.toQueryString
   * @return {ResultValueObjectFactory}
   */
  constructor({
    config,
    fetchOrFail,
    fromDealerRawDataToDealerEntityMapper,
    fromDealersListRawDataToDealersListValueObjectMapper,
    contactDealerErrorFactory,
    callMeNowDealerErrorFactory,
    resultValueObjectFactory,
    trackingPhoneDealerValueObjectFactory,
    trackingPhoneErrorFactory,
    getDealerErrorFactory,
    searchResultsDealerErrorFactory,
    toQueryString
  }) {
    super()

    this._config = config
    this._fetchOrFail = fetchOrFail
    this._fromDealerRawDataToDealerEntityMapper = fromDealerRawDataToDealerEntityMapper
    this._fromDealersListRawDataToDealersListValueObjectMapper = fromDealersListRawDataToDealersListValueObjectMapper
    this._contactDealerErrorFactory = contactDealerErrorFactory
    this._callMeNowDealerErrorFactory = callMeNowDealerErrorFactory
    this._resultValueObjectFactory = resultValueObjectFactory
    this._trackingPhoneDealerValueObjectFactory = trackingPhoneDealerValueObjectFactory
    this._trackingPhoneErrorFactory = trackingPhoneErrorFactory
    this._getDealerErrorFactory = getDealerErrorFactory
    this._searchResultsDealerErrorFactory = searchResultsDealerErrorFactory
    this._toQueryString = toQueryString
  }

  /**
   * @method
   * @param {Object} param
   * @param {TrackingPhoneRequestParamsDealerValueObject} trackingPhoneRequestParamsVO
   * @return {TrackingPhoneDealerValueObject}
   */
  @inlineError
  async getTrackingPhone({trackingPhoneRequestParamsVO: requestVO}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const params = requestVO.getRequestParams()
    const isAdTrackingPhone = requestVO.isAdTrackingPhone()

    const pathName = isAdTrackingPhone
      ? `/vehicle/tracking-phone`
      : `/dealers/${requestVO.getContractId()}/tracking-phone`

    const endpointUrl = `${this._config.get('API_URL')}${pathName}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: ({message}) => {
          return this._trackingPhoneErrorFactory(
            "[HTTPDealerRepository#getTrackingPhone] Tracking phone can't be fetched"
          )
        }
      })
      .post(endpointUrl, params, {withCredentials: true})

    if (error) return Promise.reject(error)

    const {data} = response
    const {phone, phoneNumber, sessionId, trackingId} = data
    return this._trackingPhoneDealerValueObjectFactory({phoneNumber: phone ?? phoneNumber, sessionId, trackingId})
  }

  @inlineError
  getIsMakeHired({getIsMakeHiredRequest}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const url = `${this._config.get('API_URL')}/dealers/hired-makes/${getIsMakeHiredRequest.makeId}`
    return this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._getDealerErrorFactory('Dealer information can not be fetched')
      })
      .get(url)
      .then(([error, response]) => {
        if (error) return Promise.reject(error)
        const {data} = response
        return GetIsMakeHiredValueObject.createFromApi(data)
      })
  }

  @inlineError
  getDealer({requestVO}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {contractId} = requestVO.toJSON()

    return this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._getDealerErrorFactory('Dealer information can not be fetched')
      })
      .get(`${this._config.get('API_URL')}/dealers/${contractId}`)
      .then(([error, response]) => {
        if (error) return Promise.reject(error)

        const {data} = response
        return this._fromDealerRawDataToDealerEntityMapper.map(data)
      })
  }

  @inlineError
  async getSearchResults({requestVO}) {
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const {filters, pagination, sort} = requestVO.toJSON()

    const url = `${this._config.get('API_URL')}/v2/dealers/search`
    const body = {filters, pagination, sort}

    return this._fetchOrFail
      .withErrorsFactories({
        [GENERIC]: () => this._searchResultsDealerErrorFactory('Dealers list can not be fetched')
      })
      .post(url, body)
      .then(([error, response]) => {
        if (error) return Promise.reject(error)
        const {data} = response
        return this._fromDealersListRawDataToDealersListValueObjectMapper.map(data)
      })
  }
}
